import { AgentAvatar } from "./AgentAvatar.js";
import { agentById, type AgentId } from "./nav.js";
import { relativeTime, type RecentChat } from "./sidebar-stubs.js";
import { cn } from "./cn.js";

type Props = {
	recents: RecentChat[];
	activeAgentId: AgentId | null;
	chatOpen: boolean;
	/** Agent ids with an in-flight reply (show spinner on the row). */
	busyAgentIds?: ReadonlySet<AgentId>;
	onOpen: (id: AgentId) => void;
	onOpenTeam: () => void;
};

/** Recent agent threads — not the full roster. */
export function SidebarRecents({
	recents,
	activeAgentId,
	chatOpen,
	busyAgentIds,
	onOpen,
	onOpenTeam,
}: Props) {
	return (
		<section className="section" aria-label="Recent chats">
			<div className="section-head">
				<h2 className="section-title">Recent</h2>
				<span className="section-meta">{recents.length}</span>
			</div>

			{recents.length === 0 ? (
				<p className="side-empty">
					Chats you open show up here.
				</p>
			) : (
				<ul className="side-list">
					{recents.map((row) => {
						const agent = agentById(row.agentId);
						const active =
							chatOpen && activeAgentId === row.agentId;
						const busy = busyAgentIds?.has(row.agentId) ?? false;
						return (
							<li key={row.agentId}>
								<button
									type="button"
									className={cn(
										"side-recent-row",
										active && "side-recent-row--active",
										busy && "side-recent-row--busy",
									)}
									onClick={() => onOpen(row.agentId)}
									aria-busy={busy || undefined}
								>
									<span className="side-item-mark">
										{busy ? (
											<span
												className="side-recent-spinner"
												aria-hidden
											/>
										) : (
											<AgentAvatar agent={agent} size="sm" />
										)}
									</span>
									<span className="side-recent-copy">
										<span className="side-recent-title">
											<span className="side-item-name">
												{agent.name}
											</span>
											<span className="side-recent-time">
												{busy ? "Working…" : relativeTime(row.at)}
											</span>
										</span>
										<span className="side-recent-preview">
											{row.preview}
										</span>
									</span>
								</button>
							</li>
						);
					})}
				</ul>
			)}

			<button type="button" className="side-more" onClick={onOpenTeam}>
				Your team
			</button>
		</section>
	);
}
